// Real Optro work step listing for the import wizard's select step, shaped
// like useOptroImportControls: server pagination, server-side search, and
// an audit/not-imported filter. Source: GET /api/v1/ops_audit_items via the
// auditboard client (backend resolves audit/section/subsection/preparer
// joins and returns the summarized rows below).
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchFastApi } from "../api/fastapi";
import {
  ListOptroWorkStepsResponseSchema,
  OptroWorkStepSearchBucket,
  OptroWorkStepSummary,
  SearchOptroWorkStepsResponseSchema,
} from "../models/audit/workStep";
import { useDebounce } from "./useDebounce";

export const OPTRO_WORK_STEP_IMPORT_PAGE_SIZE = 50;

export interface OptroWorkStepFilters {
  opsAuditId: number | null;
  excludeLinked: boolean;
}

// Mirrors buildListParams in useOptroImportControls.ts.
//
// Pagination is driven by the server's `nextOffset` / `hasMore` rather than
// derived from `total` / item count: when `excludeLinked` is set the backend
// refills pages by skipping ahead past linked Optro rows, so the number of
// rows returned no longer maps 1:1 onto Optro's own offset.
function buildListParams(
  search: string,
  filters: OptroWorkStepFilters,
  offset: number,
  limit: number,
): URLSearchParams {
  const params = new URLSearchParams({
    search,
    limit: String(limit),
    offset: String(offset),
    exclude_linked: String(filters.excludeLinked),
  });
  if (filters.opsAuditId !== null) {
    params.set("ops_audit_id", String(filters.opsAuditId));
  }
  return params;
}

export function useOptroImportWorkSteps(
  search: string,
  filters: OptroWorkStepFilters,
) {
  const debouncedSearch = useDebounce(search, 300);
  const { opsAuditId, excludeLinked } = filters;

  const [workSteps, setWorkSteps] = useState<OptroWorkStepSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // False until the very first fetch settles — lets the page distinguish the
  // first-ever load (full interstitial) from a search/filter refetch (dimmed
  // table + spinner overlay) — without this, clearing a search that matched
  // nothing looked like a first load again.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // The Optro offset the next page should start from, as reported by the
  // server. Can't be derived from `workSteps.length` because `excludeLinked`
  // refills a page across multiple Optro pages server-side.
  const nextOffsetRef = useRef(0);
  // Incremented on every reset fetch; used to discard results from a reset
  // fetch that has since been superseded by a newer search term or filter
  // change, so a slow in-flight `loadMore` response can't append stale rows
  // onto the new list or stomp `nextOffsetRef`.
  const generationRef = useRef(0);

  // Treat unmount as a superseding event: bumping the generation makes every
  // in-flight fetch's stale checks discard its response instead of setting
  // state on the unmounted hook — the same protection the `cancelled` flag
  // gives `useOptroWorkStepFilterOptions` below.
  useEffect(() => {
    return () => {
      generationRef.current += 1;
    };
  }, []);

  const fetchPage = useCallback(
    async (offset: number, replace: boolean) => {
      if (replace) {
        generationRef.current += 1;
      }
      const generation = generationRef.current;

      if (replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(false);
      try {
        const params = buildListParams(
          debouncedSearch,
          { opsAuditId, excludeLinked },
          offset,
          OPTRO_WORK_STEP_IMPORT_PAGE_SIZE,
        );

        const response = await fetchFastApi({
          method: "GET",
          path: `/work-steps/external/work-steps?${params.toString()}`,
        });
        // Discard results from a superseded reset fetch.
        if (generationRef.current !== generation) return;
        const parsed = ListOptroWorkStepsResponseSchema.parse(response);
        setWorkSteps((prev) =>
          replace ? parsed.workSteps : [...prev, ...parsed.workSteps],
        );
        setTotal(parsed.total);
        setHasMore(parsed.hasMore);
        nextOffsetRef.current = parsed.nextOffset;
      } catch (err) {
        if (generationRef.current !== generation) return;
        console.error("Failed to load Optro work steps:", err);
        setError(true);
      } finally {
        if (replace) {
          // Only clear `loading` if a newer reset hasn't already taken
          // over; otherwise this stale call would flip it back to false
          // while the newer reset is still in flight.
          if (generationRef.current === generation) {
            setLoading(false);
            setHasLoadedOnce(true);
          }
        } else {
          setLoadingMore(false);
        }
      }
    },
    [debouncedSearch, opsAuditId, excludeLinked],
  );

  useEffect(() => {
    nextOffsetRef.current = 0;
    void fetchPage(0, true);
  }, [fetchPage, attempt]);

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      void fetchPage(nextOffsetRef.current, false);
    }
  }, [loading, loadingMore, hasMore, fetchPage]);

  const workStepsRef = useRef<OptroWorkStepSummary[]>([]);
  const hasMoreRef = useRef(false);
  // Refs mirroring state that fetchAllRemaining's loop needs current values
  // of without re-creating the callback per render.
  useEffect(() => {
    workStepsRef.current = workSteps;
  }, [workSteps]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Fetches every remaining page for the current search/filters (used by the
  // header select-all) and returns the COMPLETE matching list — already-loaded
  // rows plus everything fetched here. Pages land in `workSteps` as they
  // arrive, so the table fills up alongside the selection. Returns
  // "superseded" (silently) when a search/filter change invalidates the loop
  // mid-flight, or "error" if a page fails.
  const fetchAllRemaining = useCallback(async (): Promise<
    | { status: "ok"; workSteps: OptroWorkStepSummary[] }
    | { status: "superseded" }
    | { status: "error" }
  > => {
    const generation = generationRef.current;
    const all = [...workStepsRef.current];
    if (!hasMoreRef.current) {
      return { status: "ok", workSteps: all };
    }

    // Use the listing endpoint's maximum page size so select-all drains in
    // the fewest requests.
    const SELECT_ALL_PAGE_SIZE = 100;
    const MAX_PAGES = 200; // safety backstop; 20k rows is beyond any real tenant
    let offset = nextOffsetRef.current;
    setLoadingMore(true);
    try {
      for (let page = 0; page < MAX_PAGES; page++) {
        const params = buildListParams(
          debouncedSearch,
          { opsAuditId, excludeLinked },
          offset,
          SELECT_ALL_PAGE_SIZE,
        );

        const response = await fetchFastApi({
          method: "GET",
          path: `/work-steps/external/work-steps?${params.toString()}`,
        });
        if (generationRef.current !== generation) {
          return { status: "superseded" };
        }
        const parsed = ListOptroWorkStepsResponseSchema.parse(response);
        all.push(...parsed.workSteps);
        offset = parsed.nextOffset;
        nextOffsetRef.current = parsed.nextOffset;
        setWorkSteps([...all]);
        setTotal(parsed.total);
        setHasMore(parsed.hasMore);
        if (!parsed.hasMore) {
          return { status: "ok", workSteps: all };
        }
      }
      // MAX_PAGES exhausted without the server reporting the end.
      console.error(
        "Select-all aborted: page cap reached before hasMore=false",
      );
      return { status: "error" };
    } catch (err) {
      if (generationRef.current !== generation) {
        return { status: "superseded" };
      }
      console.error("Failed to load all matching Optro work steps:", err);
      return { status: "error" };
    } finally {
      if (generationRef.current === generation) {
        setLoadingMore(false);
      }
    }
  }, [debouncedSearch, opsAuditId, excludeLinked]);

  return {
    workSteps,
    total,
    loading,
    loadingMore,
    hasLoadedOnce,
    error,
    retry: () => setAttempt((n) => n + 1),
    loadMore,
    hasMore,
    fetchAllRemaining,
  };
}

// Batched cross-audit search for the select step: one request per settled
// term covering EVERY selected audit (GET /work-steps/external/work-steps/
// search), instead of the per-group listing's one-request-per-audit fan-out.
// Returns one bucket per audit — its first matches plus the full match count.
// Inert while the term is blank.
export function useOptroWorkStepSearch(term: string, auditIds: number[]) {
  const debouncedTerm = useDebounce(term, 300);
  const [buckets, setBuckets] = useState<OptroWorkStepSearchBucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Same stale-response guard as useOptroImportWorkSteps: bumped per fetch
  // (and on unmount) so a superseded response can never land.
  const generationRef = useRef(0);
  useEffect(() => {
    return () => {
      generationRef.current += 1;
    };
  }, []);

  // Join for a stable effect dependency; ids come from step 1's selection,
  // which is fixed while this page is mounted.
  const idsKey = auditIds.join(",");

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    const settled = debouncedTerm.trim();
    if (settled === "" || idsKey === "") {
      setBuckets([]);
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    void (async () => {
      try {
        const params = new URLSearchParams({
          term: settled,
          ops_audit_ids: idsKey,
        });
        const response = await fetchFastApi({
          method: "GET",
          path: `/work-steps/external/work-steps/search?${params.toString()}`,
        });
        if (generationRef.current !== generation) return;
        const parsed = SearchOptroWorkStepsResponseSchema.parse(response);
        setBuckets(parsed.groups);
      } catch (err) {
        if (generationRef.current !== generation) return;
        console.error("Failed to search Optro work steps:", err);
        setError(true);
      } finally {
        if (generationRef.current === generation) {
          setLoading(false);
        }
      }
    })();
  }, [debouncedTerm, idsKey, attempt]);

  return {
    buckets,
    // Loading covers both the in-flight request and the debounce gap, so the
    // page can show one coherent pending state from the first keystroke.
    loading: loading || debouncedTerm.trim() !== term.trim(),
    error,
    retry: () => setAttempt((n) => n + 1),
  };
}
